import { useMemo } from 'react';
import { useLocation, Outlet } from 'react-router';
import {
    LayoutGrid01,
    Atom02,
    Package,
    Users01,
    UserPlus01,
    BookOpen01,
    Settings01,
    HelpCircle,
    Bell01,
    Calendar,
    ShoppingCart01,
    Globe02
} from '@untitledui/icons';
import { SidebarNavigationSimple } from '@/components/application/app-navigation/sidebar-navigation/sidebar-simple';
import { AICTACard } from '@/components/application/ai-chat/ai-cta-card';
import type { NavItemType } from '@/components/application/app-navigation/config';
import { useTranslation } from 'react-i18next';
import { useMenuConfiguration } from '@/hooks/useMenuConfiguration';
import { MenuRouteGuard } from '@/components/application/route-guard/menu-route-guard';
import { useActivityUnreadCount } from '@/hooks/api/use-activities';

// Estendi NavItemType per includere key
type NavItemWithKey = NavItemType & { key?: string };

export const ReceiverLayout = () => {
    const location = useLocation();
    const currentPath = location.pathname;
    const { t } = useTranslation('navigation');
    const { isMenuItemEnabled } = useMenuConfiguration();

    // Fetch unread count for activity badge
    const { data: unreadCount = 0 } = useActivityUnreadCount('receiver');

    // Navigation items for sidebar (con chiavi per il filtraggio)
    const allSidebarItems: NavItemWithKey[] = useMemo(() => [
        {
            key: 'ask-eve',
            label: t('askEve'),
            href: '/receiver/ask-eve',
            icon: Atom02
        },
        {
            key: 'dashboard',
            label: t('controlHub'),
            href: '/receiver/control-hub',
            icon: LayoutGrid01
        },
        {
            key: 'calendar',
            label: t('calendar'),
            href: '/receiver/calendar',
            icon: Calendar
        },
        {
            key: 'shippers',
            label: t('shippers'),
            href: '/receiver/shippers',
            icon: Users01
        },
        {
            key: 'containers',
            label: t('containers'),
            href: '/receiver/containers',
            icon: Package
        },
        {
            key: 'liveTracking',
            label: t('liveTracking'),
            href: '/receiver/live-tracking',
            icon: Globe02
        },
        {
            key: 'orders',
            label: t('orders'),
            href: '/receiver/orders',
            icon: ShoppingCart01
        },
        {
            key: 'activity',
            label: t('activity'),
            href: '/receiver/activity',
            icon: Bell01,
            badge: unreadCount > 0 ? (
                <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2 items-center justify-center rounded-full bg-error-600 ring-1 ring-primary">
                    <span className="sr-only">{t('unreadNotifications', { count: unreadCount })}</span>
                </span>
            ) : undefined
        },
        {
            key: 'team',
            label: t('team'),
            href: '/receiver/team',
            icon: UserPlus01
        },
        {
            key: 'addressBook',
            label: t('addressBook'),
            href: '/receiver/address-book',
            icon: BookOpen01
        },
        {
            key: 'settings',
            label: t('settings'),
            href: '/receiver/profile',
            icon: Settings01
        }
    ], [t, unreadCount]);

    // Filtra i sidebar items in base alla configurazione menu
    const sidebarItems = useMemo(() =>
        allSidebarItems.filter(item =>
            !item.key || isMenuItemEnabled(item.key)
        ),
        [allSidebarItems, isMenuItemEnabled]
    );

    // Footer items for sidebar
    const footerItems: NavItemType[] = [
        {
            label: t('support'),
            href: '#',
            icon: HelpCircle
        },
    ];

    const env = import.meta.env.VITE_ENV;
    const hasEnvironmentBanner = env !== "production";

    // Nascondi "Summon Eve" quando si è sulla pagina Ask Eve
    const isOnAskEvePage = currentPath === '/receiver/ask-eve';

    return (
        <div className={`flex h-screen bg-secondary ${hasEnvironmentBanner ? "pt-7" : ""}`}>
            {/* Sidebar Navigation */}
            <SidebarNavigationSimple
                activeUrl={currentPath}
                items={sidebarItems}
                footerItems={footerItems}
                featureCard={!isOnAskEvePage ? <AICTACard /> : undefined}
                showAccountCard={true}
                className="hidden lg:flex"
            />

            {/* Page Content */}
            <main className="flex-1 overflow-auto bg-secondary">
                <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
                    <MenuRouteGuard>
                        <Outlet />
                    </MenuRouteGuard>
                </div>
            </main>
        </div>
    );
};
